import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useTestimonials } from "@/hooks/usePortfolio";
import { SectionTitle } from "@/components/SectionTitle";

const initials = (name) => {
  if (!name) return "M";
  return name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();
};

export const Testimonials = () => {
  const { data: list, isLoading, error } = useTestimonials();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const total = list ? list.length : 0;

  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

  useEffect(() => {
    if (paused || total <= 1) return;
    const t = setInterval(next, 6500);
    return () => clearInterval(t);
  }, [next, paused, total]);

  if (isLoading || error || !list || list.length === 0) return null;

  const active = list[idx];
  const upcoming = list.length > 1 ? [list[(idx + 1) % total], list[(idx + 2) % total], list[(idx + 3) % total]].slice(0, Math.min(3, list.length - 1)) : [];

  return (
    <section
      id="testimonials"
      className="section-pad relative overflow-hidden"
      data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 -z-10 grain">
        <div className="absolute top-1/4 right-0 w-[40rem] h-[40rem] rounded-full" style={{ background: "radial-gradient(closest-side, hsl(var(--gold)/0.1), transparent)" }} />
      </div>

      <div className="container-x">
        <SectionTitle eyebrow="Endorsements" title={<>Words from <span className="italic gold-text">colleagues & clients</span></>} subtitle="Voices from peers, leaders and partners who have worked alongside through the journey." />

        <div className="grid lg:grid-cols-12 gap-8 max-w-6xl mx-auto items-stretch">
          {/* Featured editorial-style quote */}
          <div className="lg:col-span-8 relative flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
                className="glass rounded-3xl p-8 md:p-12 grain relative overflow-hidden flex-1"
                data-testid={`testimonial-active-${active.id}`}
              >
                <Quote className="absolute top-4 right-4 w-20 h-20 md:top-8 md:right-10 md:w-32 md:h-32 text-[hsl(var(--gold))]/8" strokeWidth={1} />
                <div className="relative z-10">
                  <div className="flex items-center gap-1 mb-6">
                    {Array.from({ length: Math.round(active.rating || 5) }).map((_, k) => (
                      <Star key={k} className="w-4 h-4 fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" />
                    ))}
                  </div>
                  <p className="font-serif text-2xl md:text-3xl leading-[1.4] text-foreground/95" style={{ textWrap: "balance" }}>
                    "{active.testimonialText}"
                  </p>
                  <div className="hairline my-8" />
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[hsl(var(--gold-light))] to-[hsl(var(--gold-deep))] flex items-center justify-center shrink-0">
                      <span className="font-serif text-[hsl(var(--navy-deep))] font-bold text-base">{initials(active.clientName)}</span>
                    </div>
                    <div>
                      <div className="font-serif text-xl">{active.clientName}</div>
                      <div className="text-sm text-muted-foreground mt-0.5">
                        {active.clientDesignation}{active.companyName ? ` · ${active.companyName}` : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controls */}
            {total > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-xs tracking-[0.25em] uppercase text-muted-foreground">
                  <span className="font-serif text-base text-foreground">{String(idx + 1).padStart(2, "0")}</span>
                  <span className="mx-2 text-[hsl(var(--gold))]">/</span>
                  <span>{String(total).padStart(2, "0")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="testimonial-prev" onClick={prev} className="w-11 h-11 rounded-full glass hover:border-[hsl(var(--gold))] transition-colors flex items-center justify-center">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button data-testid="testimonial-next" onClick={next} className="w-11 h-11 rounded-full glass hover:border-[hsl(var(--gold))] transition-colors flex items-center justify-center">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Stacked upcoming voices */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            {upcoming.length > 0 && <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground pl-1">Up Next</div>}
            {upcoming.map((t, i) => (
              <motion.button
                key={`${t.id}-${idx}-${i}`}
                onClick={() => setIdx((idx + 1 + i) % total)}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1 - i * 0.18, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                className="text-left glass rounded-2xl p-5 grain hover:border-[hsl(var(--gold))]/50 transition-all hover:-translate-y-0.5 group"
                data-testid={`testimonial-upcoming-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(var(--gold-light))]/70 to-[hsl(var(--gold-deep))]/70 flex items-center justify-center shrink-0">
                    <span className="font-serif text-[hsl(var(--navy-deep))] font-bold text-xs">{initials(t.clientName)}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-sm leading-tight truncate">{t.clientName}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5 truncate">{t.companyName || t.clientDesignation}</div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-muted-foreground leading-relaxed line-clamp-3 group-hover:text-foreground/80 transition-colors">"{t.testimonialText}"</p>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Pagination dots */}
        {total > 1 && (
          <div className="mt-10 flex items-center justify-center gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`testimonial-dot-${i}`}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[hsl(var(--gold))]" : "w-1.5 bg-[hsl(var(--foreground))]/25 hover:bg-[hsl(var(--foreground))]/40"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
